import {create} from 'zustand'
import {immer} from 'zustand/middleware/immer'
import {devtools} from 'zustand/middleware'
import {getQueryClient} from '../../lib/queryClient'
import type {TActivity, TApproval} from '../../types/project.type'
import type {TConflict} from '../../types/conflict.type'
import type {TNotice} from '../../types/notice.type'

export type WeekEntities = {
  approvals: TApproval[]
  activities: TActivity[]
  notices: TNotice[]
  movements: TActivity[]
  conflicts: TConflict[]
}

type DomainContext = {
  projectId: string
  weekNumber: string
  year: string
}

type ActivityFieldsPatch = Partial<
  Pick<TActivity, 'priority' | 'entryTracks' | 'exitTracks'>
>

interface ProjectWeekActionsState extends DomainContext {
  entities: WeekEntities
  setContext: (ctx: Partial<DomainContext>) => void
  setFromWeekData: (weekData: Partial<WeekEntities>) => void
  /**
   * Patches a single activity (or movement) by id with the provided fields.
   * Used by mutation hooks to keep the WorkView/DayView store in sync with
   * optimistic updates already applied to the React Query cache.
   */
  patchActivityFields: (activityId: string, patch: ActivityFieldsPatch) => void
}

/**
 * Prefix of the week-data query key. The query itself appends its scope
 * (`network.hooks.ts`); every INVALIDATION uses the prefix alone.
 *
 * React Query matches keys by prefix, so `['projectWeek']` reaches the query
 * whatever it is scoped by. That matters because a mutation hook rarely knows
 * the scope: it holds an activity or a window, not the week the screen happens
 * to be showing. Spelling the full key out made each site re-derive that scope
 * from the store — and skip the invalidation whenever it was not set.
 */
export const PROJECT_WEEK_QUERY_KEY = ['projectWeek'] as const

export const invalidateWeek = () => {
  const qc = getQueryClient()

  qc.invalidateQueries({queryKey: PROJECT_WEEK_QUERY_KEY})

  // Always invalidate /activities & /protections data
  qc.invalidateQueries({queryKey: ['networkActivities']})
  qc.invalidateQueries({queryKey: ['networkProtections']})
  qc.invalidateQueries({queryKey: ['activity']})
  qc.invalidateQueries({queryKey: ['group-activity']})
}

export const useProjectWeekActions = create<ProjectWeekActionsState>()(
  devtools(
    immer(set => ({
      projectId: '',
      weekNumber: '',
      year: '',
      entities: {
        approvals: [],
        activities: [],
        notices: [],
        movements: [],
        conflicts: [],
      },

      setContext: (ctx: Partial<DomainContext>) => {
        set(state => {
          state.projectId = ctx.projectId ?? state.projectId
          state.weekNumber = ctx.weekNumber ?? state.weekNumber
          state.year = ctx.year ?? state.year
        })
      },

      setFromWeekData: (weekData: Partial<WeekEntities>) => {
        set(state => {
          state.entities.approvals =
            weekData.approvals ?? state.entities.approvals
          state.entities.activities =
            weekData.activities ?? state.entities.activities
          state.entities.notices = weekData.notices ?? state.entities.notices
          state.entities.movements =
            weekData.movements ?? state.entities.movements
          state.entities.conflicts =
            weekData.conflicts ?? state.entities.conflicts
        })
      },

      patchActivityFields: (activityId: string, patch: ActivityFieldsPatch) => {
        set(state => {
          // Apply to whichever bucket holds the activity. Movements live in
          // `movements`, work activities in `activities` — never both.
          for (const list of [
            state.entities.activities,
            state.entities.movements,
          ]) {
            const target = list.find(a => a.id === activityId)
            if (target) {
              Object.assign(target, patch)
              return
            }
          }
        })
      },
    })),
  ),
)
